import type { ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import {
  BookOpen,
  CircleHelp,
  Bus,
  CalendarRange,
  ClipboardCheck,
  Clock4,
  FileText,
  History,
  LayoutDashboard,
  Network,
  ScanLine,
  Stethoscope,
  Users,
  Wrench,
  X,
  type LucideProps,
} from 'lucide-react';
import type { Permission } from '@parkline/shared';

import { useAuth } from '../lib/auth';

export interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  /** Пункт виден, только если у роли есть это право (ТЗ п.2). Без права — виден всем. */
  permission?: Permission;
  /** Логическая группа для визуального разделителя. */
  group: 'guide' | 'operations' | 'stations' | 'admin';
}

export const NAV_ITEMS: readonly NavItem[] = [
  // Справка доступна всем без права: новому сотруднику нужно понять, что делать именно ему.
  { to: '/guide', label: 'Как это работает', icon: CircleHelp, group: 'guide' },
  { to: '/board', label: 'Доска выпуска', icon: LayoutDashboard, permission: 'release:read', group: 'operations' },
  { to: '/releases', label: 'Выпуски', icon: CalendarRange, permission: 'release:read', group: 'operations' },
  { to: '/shifts', label: 'Смены', icon: Clock4, permission: 'shift:read', group: 'operations' },
  { to: '/waybills', label: 'Путевые листы', icon: FileText, permission: 'waybill:read', group: 'operations' },
  { to: '/assignments', label: 'Наряды', icon: ClipboardCheck, permission: 'assignment:read', group: 'operations' },
  { to: '/mechanic', label: 'Техосмотр', icon: Wrench, permission: 'technical:write', group: 'stations' },
  { to: '/medic', label: 'Медосмотр', icon: Stethoscope, permission: 'medical:write', group: 'stations' },
  { to: '/terminal', label: 'Терминал', icon: ScanLine, permission: 'shift:read', group: 'stations' },
  { to: '/driver', label: 'Моя смена', icon: Bus, permission: 'assignment:respond', group: 'stations' },
  { to: '/directories', label: 'Справочники', icon: BookOpen, permission: 'directory:read', group: 'admin' },
  { to: '/users', label: 'Пользователи', icon: Users, permission: 'user:manage', group: 'admin' },
  // Журнал интеграций закрыт audit:read, а не waybill:read: последнее есть у водителя,
  // а служебный обмен с ЕСУТД и тексты ошибок стенда ему видеть незачем.
  { to: '/integrations', label: 'Журнал интеграций', icon: Network, permission: 'audit:read', group: 'admin' },
  { to: '/audit', label: 'Аудит', icon: History, permission: 'audit:read', group: 'admin' },
];

export interface SidebarProps {
  /** Открыт ли выдвижной вариант на узком экране. */
  open: boolean;
  onClose: () => void;
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const { can } = useAuth();
  const items = NAV_ITEMS.filter((item) => !item.permission || can(item.permission));

  return (
    <>
      {open ? (
        <button
          type="button"
          aria-label="Закрыть меню"
          className="fixed inset-0 z-30 cursor-default bg-slate-900/40 lg:hidden"
          onClick={onClose}
        />
      ) : null}

      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white',
          'transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center justify-between px-4 py-3 lg:hidden">
          <span className="font-semibold text-brand-800">Parkline AI</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть меню"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {items.length === 0 ? (
            <p className="px-3 py-2 text-sm text-slate-500">Разделы недоступны: роли не назначены.</p>
          ) : null}
          <ul className="flex flex-col gap-0.5">
            {items.map((item, index) => (
              <li key={item.to}>
                {index > 0 && items[index - 1].group !== item.group ? (
                  <hr className="my-2 border-slate-200" />
                ) : null}
                <NavLink
                  to={item.to}
                  onClick={onClose}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-brand-50 text-brand-800'
                        : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900',
                    )
                  }
                >
                  <item.icon className="h-5 w-5 shrink-0" aria-hidden />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </>
  );
}
